import dataset from "../data/knowledge_data.json";

export const SIMILARITY_THRESHOLD = 0.15;

const FALLBACK_RESPONSE =
  "Sorry, I couldn't understand your question. Please ask about CBAM forms, emissions calculations, or precursors.";

let cachedDataset = [];
let vocabularyArray = [];
let vocabularyIndexMap = {};
let idfMap = {};
let vectorizedDataset = [];
let isInitialized = false;
let initializationPromise = null;

const WORD_NORMALIZATION_MAP = {
  // CBAM core terms
  cbam: "cbam",
  "carbon border": "cbam",

  // Emissions terminology
  emission: "emissions",
  emissions: "emissions",
  ghg: "emissions",
  co2: "emissions",
  co2e: "emissions",
  tco2e: "emissions",
  carbon: "emissions",

  // Direct/Indirect
  direct: "direct",
  indirect: "indirect",
  scope1: "direct",
  scope2: "indirect",

  // SEE
  see: "see",
  "specific embedded emissions": "see",
  embedded: "see",

  // Forms
  forma: "form a",
  formb: "form b",
  formc: "form c",
  formd: "form d",
  forme: "form e",
  facility: "manufacturing facility",
  precursor: "precursor",
  precursors: "precursor",
  process: "production process",
  processes: "production process",
  stream: "source stream",
  streams: "source stream",

  // Case 1 / Case 2
  case1: "measured",
  case2: "default",
  measured: "measured",
  default: "default",
  cncode: "cn code",

  // Sectors
  cement: "cement",
  steel: "steel",
  aluminium: "aluminium",
  aluminum: "aluminium",
  fertiliser: "fertiliser",
  fertilizer: "fertiliser",
  ammonia: "fertiliser",
  hydrogen: "hydrogen",
  electricity: "electricity",

  // Reports / status
  draft: "draft",
  submitted: "submitted",
  duplicate: "duplicate",
  report: "report",
  audit: "report",

  // Cost
  cost: "cost",
  price: "cost",
  exposure: "cost",

  // Control balance
  balance: "control balance",
  control: "control balance",
};

const loadDataset = async () => {
  try {
    if (Array.isArray(dataset)) {
      console.log(
        `[CosineSimilarity] ✅ Dataset imported successfully: ${dataset.length} entries`,
      );
      return dataset;
    }

    console.warn("[CosineSimilarity] ⚠️ Imported dataset is invalid");
    return [];
  } catch (error) {
    console.error("[CosineSimilarity] ❌ Error loading dataset:", error);
    return [];
  }
};

const normalizeWord = (word) => WORD_NORMALIZATION_MAP[word] || word;

export const preprocessText = (text) => {
  if (!text) {
    return "";
  }

  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map(normalizeWord)
    .join(" ");
};

const tokenize = (text) => text.split(/\s+/).filter(Boolean);

const calculateTF = (tokens) => {
  if (!tokens.length) {
    return {};
  }

  const tf = {};
  tokens.forEach((token) => {
    tf[token] = (tf[token] || 0) + 1;
  });

  Object.keys(tf).forEach((token) => {
    tf[token] /= tokens.length;
  });

  return tf;
};

const calculateIDF = (tokenizedDocs) => {
  const idf = {};
  const docFrequency = {};
  const totalDocs = tokenizedDocs.length;

  tokenizedDocs.forEach((tokens) => {
    new Set(tokens).forEach((token) => {
      docFrequency[token] = (docFrequency[token] || 0) + 1;
    });
  });

  Object.keys(docFrequency).forEach((token) => {
    idf[token] = Math.log((totalDocs + 1) / (docFrequency[token] + 1)) + 1;
  });

  return idf;
};

const buildVocabulary = (allTokens) => Array.from(new Set(allTokens)).sort();

const createVocabularyIndexMap = (vocabulary) =>
  vocabulary.reduce((acc, token, index) => {
    acc[token] = index;
    return acc;
  }, {});

const createTFIDFVector = (tokens, vocabulary, idfLookup, vocabularyLookup) => {
  const vector = new Array(vocabulary.length).fill(0);

  if (!tokens.length || !vocabulary.length) {
    return vector;
  }

  const tf = calculateTF(tokens);

  Object.keys(tf).forEach((token) => {
    const index = vocabularyLookup[token];
    if (index !== undefined && idfLookup[token]) {
      vector[index] = tf[token] * idfLookup[token];
    }
  });

  return vector;
};

const cosineSimilarity = (vector1, vector2) => {
  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;

  for (let i = 0; i < vector1.length; i += 1) {
    dotProduct += vector1[i] * vector2[i];
    magnitude1 += vector1[i] * vector1[i];
    magnitude2 += vector2[i] * vector2[i];
  }

  if (magnitude1 === 0 || magnitude2 === 0) {
    return 0;
  }

  return dotProduct / (Math.sqrt(magnitude1) * Math.sqrt(magnitude2));
};

const vectorizeDataset = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn("[CosineSimilarity] Empty dataset received for vectorization");
    return {
      vectors: [],
      vocabulary: [],
      vocabularyLookup: {},
      idf: {},
    };
  }

  const tokenizedDocs = [];
  const allTokens = [];

  data.forEach((entry) => {
    const combinedText = [
      entry?.text || "",
      ...(entry?.patterns || []),
      ...(entry?.tags || []),
    ].join(" ");

    const tokens = tokenize(preprocessText(combinedText));
    tokenizedDocs.push(tokens);
    allTokens.push(...tokens);
  });

  const vocabulary = buildVocabulary(allTokens);
  const vocabularyLookup = createVocabularyIndexMap(vocabulary);
  const idf = calculateIDF(tokenizedDocs);
  const vectors = tokenizedDocs.map((tokens) =>
    createTFIDFVector(tokens, vocabulary, idf, vocabularyLookup),
  );

  console.log(
    `[CosineSimilarity] Vectorized dataset with vocabulary size ${vocabulary.length}`,
  );

  return { vectors, vocabulary, vocabularyLookup, idf };
};

const initialize = async () => {
  if (isInitialized) {
    return true;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      console.log("[CosineSimilarity] 🔄 Initializing dataset...");
      const loadedData = await loadDataset();

      if (!Array.isArray(loadedData) || loadedData.length === 0) {
        console.warn("[CosineSimilarity] ⚠️ Dataset is empty or invalid");
        cachedDataset = [];
        isInitialized = false;
        return false;
      }

      console.log(
        `[CosineSimilarity] 📊 Processing ${loadedData.length} entries...`,
      );
      const { vectors, vocabulary, vocabularyLookup, idf } =
        vectorizeDataset(loadedData);

      cachedDataset = loadedData;
      vectorizedDataset = vectors;
      vocabularyArray = vocabulary;
      vocabularyIndexMap = vocabularyLookup;
      idfMap = idf;
      isInitialized = vectors.length > 0 && vocabulary.length > 0;

      if (isInitialized) {
        console.log(
          `[CosineSimilarity] ✅ Initialization complete: ${vocabulary.length} vocabulary terms, ${vectors.length} vectors`,
        );
      } else {
        console.warn(
          "[CosineSimilarity] ⚠️ Initialization failed: insufficient data",
        );
      }

      return isInitialized;
    } catch (error) {
      console.error("[CosineSimilarity] ❌ Initialization error:", error);
      cachedDataset = [];
      vectorizedDataset = [];
      vocabularyArray = [];
      vocabularyIndexMap = {};
      idfMap = {};
      isInitialized = false;
      return false;
    } finally {
      initializationPromise = null;
    }
  })();

  return initializationPromise;
};

const createFallbackResult = () => ({
  response: FALLBACK_RESPONSE,
  score: 0,
  matchedEntry: null,
});

export const getBestMatch = async (userInput) => {
  try {
    const initialized = await initialize();

    if (
      !initialized ||
      !cachedDataset.length ||
      !vectorizedDataset.length ||
      !vocabularyArray.length
    ) {
      console.warn("[CosineSimilarity] ⚠️ Match skipped: dataset unavailable");
      return createFallbackResult();
    }

    const preprocessedInput = preprocessText(userInput);
    const inputTokens = tokenize(preprocessedInput);

    console.log(`[CosineSimilarity] Input: "${userInput}"`);
    console.log(`[CosineSimilarity] Preprocessed: "${preprocessedInput}"`);
    console.log(`[CosineSimilarity] Tokens: [${inputTokens.join(", ")}]`);

    if (!inputTokens.length) {
      console.warn("[CosineSimilarity] ⚠️ Empty query after preprocessing");
      return createFallbackResult();
    }

    const userVector = createTFIDFVector(
      inputTokens,
      vocabularyArray,
      idfMap,
      vocabularyIndexMap,
    );

    let bestScore = -1;
    let bestIndex = -1;

    vectorizedDataset.forEach((vector, index) => {
      const score = cosineSimilarity(userVector, vector);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });

    const normalizedScore = Number(Math.max(bestScore, 0).toFixed(4));
    const matchedEntry = bestIndex >= 0 ? cachedDataset[bestIndex] : null;

    console.log(
      `[CosineSimilarity] Best score: ${normalizedScore} / ${SIMILARITY_THRESHOLD}`,
    );
    if (matchedEntry) {
      console.log(
        `[CosineSimilarity] Matched: "${matchedEntry.response?.substring(0, 50)}..."`,
      );
    }

    if (!matchedEntry || normalizedScore < SIMILARITY_THRESHOLD) {
      console.log("[CosineSimilarity] ❌ No match: score below threshold");
      return createFallbackResult();
    }

    console.log("[CosineSimilarity] ✅ Match found!");
    return {
      response: matchedEntry.response || FALLBACK_RESPONSE,
      score: normalizedScore,
      matchedEntry: {
        text: matchedEntry.text,
        patterns: matchedEntry.patterns,
        tags: matchedEntry.tags,
      },
    };
  } catch (error) {
    console.error("[CosineSimilarity] ❌ Error in getBestMatch:", error);
    return createFallbackResult();
  }
};

export const getTopMatches = async (userInput, topN = 3) => {
  try {
    const initialized = await initialize();

    if (
      !initialized ||
      !cachedDataset.length ||
      !vectorizedDataset.length ||
      !vocabularyArray.length
    ) {
      return [];
    }

    const preprocessedInput = preprocessText(userInput);
    const inputTokens = tokenize(preprocessedInput);

    if (!inputTokens.length) {
      return [];
    }

    const userVector = createTFIDFVector(
      inputTokens,
      vocabularyArray,
      idfMap,
      vocabularyIndexMap,
    );

    return vectorizedDataset
      .map((vector, index) => ({
        score: cosineSimilarity(userVector, vector),
        entry: cachedDataset[index],
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, topN)
      .map(({ score, entry }) => ({
        response: entry?.response || "",
        score: Number(score.toFixed(4)),
        matchedEntry: {
          text: entry?.text,
          patterns: entry?.patterns,
          tags: entry?.tags,
        },
      }));
  } catch (error) {
    console.error("[CosineSimilarity] Error in getTopMatches:", error);
    return [];
  }
};
